import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-24 px-4">
          <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
            <i className="ri-question-line text-secondary-500 text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章不存在</h1>
          <p className="text-sm text-foreground-500 mb-6">该文章未找到，请返回首页浏览其他内容。</p>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = getRelatedArticles(article.slug, article.categorySlug, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.summary,
    "author": {
      "@type": "Organization",
      "name": article.author
    },
    "datePublished": article.date,
    "inLanguage": "zh-CN",
    "url": `https://sp-guide.readdy.ai/article/${article.slug}`,
    "publisher": {
      "@type": "Organization",
      "name": "SP完全指南",
      "url": "https://sp-guide.readdy.ai/"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://sp-guide.readdy.ai/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": category?.name || "分类",
        "item": `https://sp-guide.readdy.ai/category/${article.categorySlug}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - SP完全指南`}
        description={article.summary}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://sp-guide.readdy.ai/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogType="article"
      />
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: category?.name || "分类", path: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          {/* Article Content */}
          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              {/* Article Header */}
              <header className="mb-8">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="inline-block mb-4 px-3 py-1.5 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                  >
                    {category.name}
                  </Link>
                )}
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Body */}
              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-base
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  prose-ul:my-4 prose-ul:pl-0 prose-ul:list-none
                  prose-li:text-foreground-700 prose-li:text-base prose-li:leading-relaxed prose-li:mb-2 prose-li:pl-6 prose-li:relative
                  [&_ul>li]:before:content-[''] [&_ul>li]:before:absolute [&_ul>li]:before:left-0 [&_ul>li]:before:top-[0.6em] [&_ul>li]:before:w-1.5 [&_ul>li]:before:h-1.5 [&_ul>li]:before:rounded-full [&_ul>li]:before:bg-primary-400
                  prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
                "
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="mt-10 pt-8 border-t border-background-200/60">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
                  相关文章推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}